/**
 * ExceptionDetailsBlock Component
 * Displays exception metadata (rationale, accepted-by, date, conditions)
 * within an expanded FindingCard per WF-5 wireframe
 */

import type { Exception } from '../types/api';
import { formatDateLong } from '../utils/formatDate';
import { PhotoHeroCard } from './PhotoHeroCard';

export interface ExceptionDetailsBlockProps {
  exception: Exception;
  /** Optional photo URLs from the associated finding for visual context */
  photoUrls?: string[];
}

export function ExceptionDetailsBlock({ exception, photoUrls }: ExceptionDetailsBlockProps) {
  return (
    <div className="mt-4 pt-3 border-t border-gray-100 dark:border-slate-800 bg-gray-50/50 dark:bg-slate-800/50 -mx-6 px-6 pb-3">
      <div className="flex items-start gap-4">
        {/* Photo hero card (square crop, links to full gallery) */}
        {photoUrls && photoUrls.length > 0 && (
          <PhotoHeroCard
            photoUrl={photoUrls[0]}
            alt="Exception finding photo"
            size="md"
            badgeLabel="Exception"
            badgeVariant="info"
            className="flex-shrink-0"
          />
        )}

        <div className="flex-1 min-w-0">
          <h4 className="text-xs font-semibold text-gray-700 dark:text-slate-300 uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            Exception Details
          </h4>

          {/* Rationale */}
          <p className="text-sm text-gray-700 dark:text-slate-300 italic mb-3">
            &ldquo;{exception.rationale}&rdquo;
          </p>
        </div>
      </div>

      {/* Metadata grid */}
      <div className="grid grid-cols-2 gap-2 text-xs">
        {/* Accepted By */}
        <div className="flex items-center gap-1.5 text-gray-600 dark:text-slate-400">
          <svg className="w-3.5 h-3.5 text-gray-400 dark:text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          <span>{exception.accepted_by_name}</span>
        </div>

        {/* Date */}
        <div className="flex items-center gap-1.5 text-gray-600 dark:text-slate-400">
          <svg className="w-3.5 h-3.5 text-gray-400 dark:text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span>
            {formatDateLong(exception.acceptance_date)}
          </span>
        </div>
      </div>

      {/* Conditions */}
      {exception.conditions && exception.conditions.length > 0 && (
        <div className="mt-3">
          <p className="text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">Conditions:</p>
          <ul className="space-y-1">
            {exception.conditions.map((condition) => (
              <li key={condition.id} className="flex items-center gap-1.5 text-xs">
                {condition.is_met ? (
                  <svg className="w-3.5 h-3.5 text-gray-500 dark:text-slate-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ) : (
                  <svg className="w-3.5 h-3.5 text-gray-500 dark:text-slate-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
                <span className={condition.is_met ? 'text-gray-700 dark:text-slate-300' : 'text-gray-600 dark:text-slate-400'}>
                  {condition.label}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default ExceptionDetailsBlock;
